import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import {
  FaMoneyBillWave,
  FaPercent,
  FaCalendarAlt,
  FaEdit,
  FaPrint,          // Added for Printing
  FaFilePdf,        // Added for PDF export
  FaCheckCircle,
  FaTimes,
  FaPaperPlane,
  FaHistory,
  FaSync,
  FaTrash,
  FaInfoCircle,
  FaExclamationTriangle,
  FaFilter,
  FaEye,
  FaReceipt,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExternalLinkAlt,
  FaSpinner,
  FaLock,
  FaDatabase,
  FaCalculator,
  FaLink,
  FaArrowRight,
  FaCrown,
  FaLayerGroup,
  FaAward,          // Added for Logo Placeholders
  FaStamp,          // Added for Stamp sections
  FaUser,           // Used in Student Info
  FaIdCard          // Used in Student Info
} from "react-icons/fa";

import "./SoloStudentExpense.css";

const StudentExpense = ({ studentData, onExpenseUpdate, onRefresh }) => {
  // Expense Section State
  const [expenseRecords, setExpenseRecords] = useState([]);
  const [expenseData, setExpenseData] = useState({
    expensePayable: "0",
    expensePaid: "",
    expensePending: "0",
    discountType: "fixed",
    discountValue: "0",
    paidMonth: moment().format("MMMM"),
    expenseType: "other",
    description: "",
  });

  // Edit Form State
  const [editFormData, setEditFormData] = useState({
    expensePayable: "0",
    expensePaid: "0",
    discountType: "percentage",
    discountValue: "0",
    discountAmount: "0",
    discountedPayable: "0",
    expensePending: "0",
  });

  // Loading States
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isProcessingChainUpdate, setIsProcessingChainUpdate] = useState(false);

  // Filter and Sort State
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterYear, setFilterYear] = useState(moment().year());
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal States
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Parent-Child Tracking State
  const [parentEntry, setParentEntry] = useState(null);
  const [isParentEdit, setIsParentEdit] = useState(false);
  const [parentTotals, setParentTotals] = useState({
    parentPayable: 0,
    parentDiscount: 0,
    parentAdjustedPayable: 0,
    totalPaid: 0,
    remainingBalance: 0,
    childCount: 0,
  });

  // Load expense data when student changes
  useEffect(() => {
    if (studentData?.studentId) {
      fetchExpenseData();
    } else {
      resetExpenseForm();
    }
  }, [studentData?.studentId]);

  // Update parent entry and totals when records change
  useEffect(() => {
    if (expenseRecords.length > 0) {
      calculateParentAndTotals();
    } else {
      setParentEntry(null);
      setParentTotals({
        parentPayable: 0,
        parentDiscount: 0,
        parentAdjustedPayable: 0,
        totalPaid: 0,
        remainingBalance: 0,
        childCount: 0,
      });
    }
  }, [expenseRecords]);

  // Calculate parent and totals
  const calculateParentAndTotals = () => {
    if (!Array.isArray(expenseRecords) || expenseRecords.length === 0) {
      return;
    }

    // Sort records by date to identify parent (first entry)
    const sortedRecords = [...expenseRecords].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    // First entry is the parent
    const parentEntry = sortedRecords[0];
    setParentEntry(parentEntry);

    // Calculate total paid from ALL entries
    const totalPaid = sortedRecords.reduce(
      (total, record) => total + parseFloat(record.expense_paid || 0),
      0
    );

    // Get parent's adjusted payable (after discount)
    const parentPayable = parseFloat(parentEntry.expense_payable) || 0;
    const parentDiscount = parseFloat(parentEntry.discount_amount) || 0;
    const parentAdjustedPayable = parentPayable - parentDiscount;

    // Calculate remaining balance
    const remainingBalance = Math.max(0, parentAdjustedPayable - totalPaid);

    setParentTotals({
      parentPayable,
      parentDiscount,
      parentAdjustedPayable,
      totalPaid,
      remainingBalance,
      childCount: sortedRecords.length - 1,
    });

    // Update form with calculated remaining balance as next payable
    updateFormWithCalculatedBalance(remainingBalance, sortedRecords);
  };

const generateExpenseReceiptHTML = (record) => {
  // Check if record exists
  if (!record) {
    console.error("Record is undefined:", record);
    return "<html><body><h1>Error: Record not found</h1></body></html>";
  }

  const currentDate = moment().format("DD-MM-YYYY hh:mm A");
  const transDate = moment(record.created_at || new Date()).format("DD-MM-YYYY");
  const payable = parseFloat(record.expense_payable) || 0;
  const paid = parseFloat(record.expense_paid) || 0;
  const discount = parseFloat(record.discount_amount) || 0;
  const balance = parseFloat(record.expense_pending) || 0;
  const netPayable = payable - discount;
  
  // Safely handle expense_type with fallback
  const expenseType = (record.expense_type || "other").toUpperCase();

  // Safely handle student data with fallbacks
  const studentId = studentData?.studentId || "N/A";
  const studentName = studentData?.studentName || "N/A";
  const fatherName = studentData?.fatherName || "N/A";
  const studentClass = studentData?.studentClass || studentData?.class || "N/A";
  const description = record.description || "General Academic Expense";

  return `
    <html>
      <head>
        <title>Expense_Receipt_${record.id || Date.now()}</title>
        <style>
          @page { size: A4; margin: 0; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; background: #f0f2f5; padding: 30px; }
          
          /* Professional Gradient Bubbles */
          .bubble { position: absolute; border-radius: 50%; filter: blur(50px); z-index: 0; pointer-events: none; }
          .b1 { width: 500px; height: 500px; background: radial-gradient(circle, rgba(26, 42, 108, 0.1) 0%, transparent 70%); top: -150px; right: -150px; }
          .b2 { width: 450px; height: 450px; background: radial-gradient(circle, rgba(178, 148, 94, 0.08) 0%, transparent 70%); bottom: -100px; left: -100px; }

          .receipt-card {
            width: 210mm;
            background: white;
            border: 1px solid #e0e0e0;
            position: relative;
            overflow: hidden;
            padding: 50px;
            box-sizing: border-box;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            margin: 0 auto;
          }

          .header { display: flex; align-items: center; border-bottom: 4px solid #1a2a6c; padding-bottom: 25px; margin-bottom: 30px; position: relative; z-index: 1;}
          .logo-container img { height: 110px; margin-right: 30px; }
          
          .school-details h1 { 
              margin: 0; color: #1a2a6c; font-size: 34px; 
              font-family: 'Times New Roman', Times, serif; font-weight: 900; 
              text-transform: uppercase; letter-spacing: 1px; 
          }
          .school-details p { margin: 4px 0; color: #b2945e; font-size: 15px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; }
          
          .receipt-id-box { margin-left: auto; text-align: right; }
          .receipt-id-box h2 { margin: 0; color: #1a2a6c; font-size: 24px; border-bottom: 3px solid #b2945e; display: inline-block; padding-bottom: 5px; }
          .receipt-id-box p { margin: 10px 0 0 0; font-family: 'Courier New', monospace; font-size: 16px; font-weight: bold; color: #333; }

          .info-grid { 
              display: grid; grid-template-columns: 1fr 1fr; gap: 20px; 
              background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(5px);
              padding: 25px; border: 1px solid #eee; border-left: 6px solid #b2945e;
              margin-bottom: 35px; position: relative; z-index: 1;
          }
          .info-item { font-size: 15px; color: #333; border-bottom: 1px dotted #ddd; padding-bottom: 8px; }
          .info-item strong { color: #1a2a6c; width: 135px; display: inline-block; font-weight: 700; }

          .fee-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; position: relative; z-index: 1; background: white; }
          .fee-table th { background: #1a2a6c; color: white; padding: 15px; text-align: left; font-size: 14px; text-transform: uppercase; }
          .fee-table td { padding: 15px; border-bottom: 1px solid #eee; font-size: 15px; color: #2c3e50; }
          
          .summary-wrapper { display: flex; justify-content: flex-end; position: relative; z-index: 1; }
          .amount-summary { width: 380px; }
          .amount-row { display: flex; justify-content: space-between; padding: 12px 0; font-size: 15px; }
          .amount-row.total { border-top: 3px solid #1a2a6c; margin-top: 10px; font-weight: 900; font-size: 20px; color: #1a2a6c; }
          .amount-row.paid { color: #27ae60; font-weight: bold; background: #f0fff4; padding: 12px; border-radius: 6px; border: 1px solid #c6f6d5; }

          .footer-legal { 
              display: flex; justify-content: space-between; align-items: flex-end; 
              margin-top: 90px; position: relative; z-index: 1;
          }
          .sig-container { text-align: center; width: 220px; }
          .sig-line { border-top: 2px solid #1a2a6c; margin-bottom: 12px; }
          
          .stamp-container { width: 220px; text-align: center; position: relative; }
          .official-stamp-img { 
              position: absolute; top: -125px; left: 20px; width: 190px; 
              opacity: 0.92; transform: rotate(-12deg); mix-blend-mode: multiply; 
          }
          .stamp-label { font-size: 11px; color: #ccc; text-transform: uppercase; font-weight: 600; display: block; margin-top: 60px; }

          .bottom-disclaimer { 
              text-align: center; font-size: 11px; color: #95a5a6; 
              margin-top: 60px; border-top: 1px solid #eee; padding-top: 20px; 
          }
        </style>
      </head>
      <body>
        <div class="receipt-card">
          <div class="bubble b1"></div>
          <div class="bubble b2"></div>

          <div class="header">
            <div class="logo-container">
              <img src="/images/l1.jpeg" alt="Logo" onerror="this.src='/images/l2.jpeg'">
            </div>
            <div class="school-details">
              <h1>DAR-E-ARQAM SCHOOL</h1>
              <p>KOTLI LOHARAN EAST CAMPUS | SIALKOT</p>
            </div>
            <div class="receipt-id-box">
              <h2>EXPENSE VOUCHER</h2>
              <p>REF: EXP-${record.id || "N/A"}</p>
            </div>
          </div>

          <div class="info-grid">
            <div class="info-item"><strong>Student ID:</strong> ${studentId}</div>
            <div class="info-item"><strong>Class:</strong> ${studentClass}</div>
            <div class="info-item"><strong>Student Name:</strong> ${studentName}</div>
            <div class="info-item"><strong>Father Name:</strong> ${fatherName}</div>
            <div class="info-item"><strong>Expense Type:</strong> ${expenseType}</div>
            <div class="info-item"><strong>Voucher Date:</strong> ${transDate}</div>
          </div>

          <table class="fee-table">
            <thead>
              <tr>
                <th>Description / Particulars</th>
                <th style="text-align: right;">Amount (PKR)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${description} (${record.paid_month || moment().format("MMMM")})</td>
                <td style="text-align: right;">${payable.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
              </tr>
              <tr>
                <td>Discount/Concession</td>
                <td style="text-align: right; color: #27ae60;">- ${discount.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
              </tr>
            </tbody>
          </table>

          <div class="summary-wrapper">
            <div class="amount-summary">
              <div class="amount-row">
                <span>Net Payable:</span>
                <span>Rs. ${netPayable.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
              <div class="amount-row paid">
                <span>Amount Paid:</span>
                <span>Rs. ${paid.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
              <div class="amount-row total">
                <span>Current Balance:</span>
                <span>Rs. ${balance.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
            </div>
          </div>

          <div class="footer-legal">
            <div class="sig-container">
              <div class="sig-line"></div>
              <strong>Accounts Office</strong>
            </div>
            <div class="stamp-container">
              <img src="/images/stamp.png" class="official-stamp-img" alt="Stamp">
              <span class="stamp-label">Official Institution Stamp</span>
            </div>
          </div>

          <div class="bottom-disclaimer">
            This is an official computer-generated expense voucher of Dar-e-Arqam School. No manual signature required if stamped.<br>
            <strong>Software Developed by:</strong> MHS Developers | Meta Hybrid Solutions
          </div>
        </div>
      </body>
    </html>
  `;
};

  const handlePrintExpense = (record) => {
    const receiptWindow = window.open("", "_blank");
    receiptWindow.document.write(generateExpenseReceiptHTML(record));
    receiptWindow.document.close();
    setTimeout(() => {
      receiptWindow.focus();
      receiptWindow.print();
    }, 500);
  };

  // Update form with calculated balance
  const updateFormWithCalculatedBalance = (remainingBalance, sortedRecords) => {
    // Get the latest record for other fields
    const latestRecord = sortedRecords[sortedRecords.length - 1];

    setExpenseData((prev) => ({
      ...prev,
      expensePayable: remainingBalance.toString(),
      expensePending: remainingBalance.toString(),
      discountType: "fixed", // Child entries don't get discounts
      discountValue: "0",
      paidMonth: latestRecord?.paid_month || moment().format("MMMM"),
      expenseType: latestRecord?.expense_type || "other",
    }));
  };

  // Effect for edit form initialization
  useEffect(() => {
    if (editingExpense && parentEntry) {
      const payable = parseFloat(editingExpense.expense_payable) || 0;
      const paid = parseFloat(editingExpense.expense_paid) || 0;
      const discountType = editingExpense.discount_type || "percentage";
      const discountValue = parseFloat(editingExpense.discount_value) || 0;
      const discountAmount = parseFloat(editingExpense.discount_amount) || 0;
      const pending = parseFloat(editingExpense.expense_pending) || 0;

      // Check if editing parent entry
      const isParent = editingExpense.id === parentEntry.id;
      setIsParentEdit(isParent);

      // Calculate discounted payable
      const discountedPayable = Math.max(0, payable - discountAmount);

      setEditFormData({
        expensePayable: payable.toString(),
        expensePaid: paid.toString(),
        discountType: discountType,
        discountValue: discountValue.toString(),
        discountAmount: discountAmount.toString(),
        discountedPayable: discountedPayable.toString(),
        expensePending: pending.toString(),
      });
    }
  }, [editingExpense, parentEntry]);

  // Reset expense form
  const resetExpenseForm = () => {
    setExpenseData({
      expensePayable: "0",
      expensePaid: "",
      expensePending: "0",
      discountType: "fixed",
      discountValue: "0",
      paidMonth: moment().format("MMMM"),
      expenseType: "other",
      description: "",
    });
    setExpenseRecords([]);
    setParentEntry(null);
    setParentTotals({
      parentPayable: 0,
      parentDiscount: 0,
      parentAdjustedPayable: 0,
      totalPaid: 0,
      remainingBalance: 0,
      childCount: 0,
    });
  };

  // Calculate discount amount for edit form
  const calculateEditDiscountAmount = (
    payable,
    discountType,
    discountValue
  ) => {
    const payableAmount = parseFloat(payable) || 0;
    const discountVal = parseFloat(discountValue) || 0;

    if (discountType === "percentage") {
      return (payableAmount * discountVal) / 100;
    } else {
      return Math.min(discountVal, payableAmount);
    }
  };

  // Calculate discounted payable for edit form
  const calculateEditDiscountedPayable = (
    payable,
    discountType,
    discountValue
  ) => {
    const payableAmount = parseFloat(payable) || 0;
    const discountAmount = calculateEditDiscountAmount(
      payable,
      discountType,
      discountValue
    );
    return payableAmount - discountAmount;
  };

  // Calculate pending amount for edit form
  const calculateEditPending = (payable, discountType, discountValue, paid) => {
    const payableAmount = parseFloat(payable) || 0;
    const paidAmount = parseFloat(paid) || 0;
    const discountedPayable = calculateEditDiscountedPayable(
      payable,
      discountType,
      discountValue
    );
    return Math.max(0, discountedPayable - paidAmount);
  };

  // Calculate total paid so far from all records
  const calculateTotalPaidSoFar = () => {
    if (!Array.isArray(expenseRecords) || expenseRecords.length === 0) {
      return 0;
    }
    return expenseRecords.reduce(
      (total, record) => total + parseFloat(record.expense_paid || 0),
      0
    );
  };

  // Calculate total payable (from parent entry)
  const calculateTotalPayable = () => {
    if (!parentEntry) return 0;

    const payable = parseFloat(parentEntry.expense_payable) || 0;
    const discountAmount = parseFloat(parentEntry.discount_amount) || 0;
    return payable - discountAmount;
  };

  // Calculate remaining balance for the user
  const calculateRemainingBalance = () => {
    const totalPayable = calculateTotalPayable();
    const totalPaid = calculateTotalPaidSoFar();
    return Math.max(0, totalPayable - totalPaid);
  };

  // Handle edit form field changes with parent-child validation
  const handleEditFieldChange = (field, value) => {
    setEditFormData((prev) => {
      const updatedData = { ...prev, [field]: value };

      // Recalculate if relevant fields changed
      if (
        field === "expensePayable" ||
        field === "discountType" ||
        field === "discountValue" ||
        field === "expensePaid"
      ) {
        const payable =
          field === "expensePayable" ? value : prev.expensePayable;
        const discountType =
          field === "discountType" ? value : prev.discountType;
        const discountValue =
          field === "discountValue" ? value : prev.discountValue;
        const paid = field === "expensePaid" ? value : prev.expensePaid;

        // Calculate discount amount
        const discountAmount = calculateEditDiscountAmount(
          payable,
          discountType,
          discountValue
        );

        // Calculate discounted payable
        const discountedPayable = calculateEditDiscountedPayable(
          payable,
          discountType,
          discountValue
        );

        // Calculate pending
        const pending = calculateEditPending(
          payable,
          discountType,
          discountValue,
          paid
        );

        // If editing parent entry, validate total payments
        if (isParentEdit) {
          const newTotalPayable = discountedPayable;
          const totalPaidExcludingParent = expenseRecords.reduce(
            (total, record) => {
              if (record.id !== editingExpense.id) {
                return total + parseFloat(record.expense_paid || 0);
              }
              return total;
            },
            0
          );

          const newTotalPaid = totalPaidExcludingParent + parseFloat(paid);

          if (newTotalPaid > newTotalPayable) {
            // Don't show warning here, just highlight in UI
            console.warn(
              `Total payments would exceed total payable: ${newTotalPaid} > ${newTotalPayable}`
            );
          }
        }

        return {
          ...updatedData,
          discountAmount: discountAmount.toString(),
          discountedPayable: discountedPayable.toString(),
          expensePending: pending.toString(),
        };
      }

      return updatedData;
    });
  };

  // Calculate discounted amount
  const calculateDiscountedExpense = (
    payable = null,
    discountType = null,
    discountValue = null
  ) => {
    const payableAmount =
      payable !== null ? payable : parseFloat(expenseData.expensePayable) || 0;
    const discountTypeVal =
      discountType !== null ? discountType : expenseData.discountType;
    const discountValueVal =
      discountValue !== null
        ? discountValue
        : parseFloat(expenseData.discountValue) || 0;

    let discountAmount = 0;
    if (discountTypeVal === "percentage") {
      discountAmount = (payableAmount * discountValueVal) / 100;
    } else {
      discountAmount = discountValueVal;
    }

    discountAmount = Math.min(discountAmount, payableAmount);
    return payableAmount - discountAmount;
  };

  // Calculate discount amount
  const calculateExpenseDiscountAmount = (
    payable = null,
    discountType = null,
    discountValue = null
  ) => {
    const payableAmount =
      payable !== null ? payable : parseFloat(expenseData.expensePayable) || 0;
    const discountTypeVal =
      discountType !== null ? discountType : expenseData.discountType;
    const discountValueVal =
      discountValue !== null
        ? discountValue
        : parseFloat(expenseData.discountValue) || 0;

    if (discountTypeVal === "percentage") {
      return (payableAmount * discountValueVal) / 100;
    } else {
      return Math.min(discountValueVal, payableAmount);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === "" || amount === null || amount === undefined) {
      return "Rs. 0.00";
    }
    return `Rs. ${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Get latest expense pending from records array
  const getLatestExpensePendingFromRecords = (records) => {
    if (!Array.isArray(records) || records.length === 0) {
      return 0;
    }

    // Sort by created_at descending to get latest record
    const sortedRecords = [...records].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    const latestRecord = sortedRecords[0];
    const pending = parseFloat(latestRecord.expense_pending);

    return isNaN(pending) ? 0 : pending;
  };

  // Get latest expense pending from state
  const getLatestExpensePending = () => {
    return getLatestExpensePendingFromRecords(expenseRecords);
  };

  // Handle expense paid change
  const handleExpensePaidChange = (value) => {
    const paidValue = parseFloat(value) || 0;
    const discountedPayable = calculateDiscountedExpense();

    let pendingAmount = discountedPayable - paidValue;
    pendingAmount = pendingAmount >= 0 ? pendingAmount : 0;

    setExpenseData((prev) => ({
      ...prev,
      expensePaid: value,
      expensePending: pendingAmount.toString(),
    }));
  };

  // Fetch expense data
  const fetchExpenseData = async () => {
    if (!studentData?.studentId) return;

    setIsLoading(true);
    try {
      console.log("Fetching expense data for student:", studentData.studentId);

      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=search-student-fee-details",
        {
          studentId: studentData.studentId,
        }
      );

      console.log("Backend response:", response.data);

      if (response.data) {
        let expenseDetails = [];

        // Handle different response formats
        if (Array.isArray(response.data.expenseDetails)) {
          expenseDetails = response.data.expenseDetails;
        } else if (
          response.data.expenseDetails &&
          typeof response.data.expenseDetails === "object"
        ) {
          // If it's an object, convert to array
          expenseDetails = Object.values(response.data.expenseDetails);
        } else if (Array.isArray(response.data)) {
          expenseDetails = response.data;
        }

        console.log("Processed expense details:", expenseDetails);

        // Sort by date
        const sortedDetails = expenseDetails.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setExpenseRecords(sortedDetails);

        if (onExpenseUpdate) {
          onExpenseUpdate(sortedDetails);
        }
      }
    } catch (error) {
      console.error("Error fetching expense data:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load expense records. Please try again.",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchExpenseData();
    if (onRefresh) onRefresh();
  };

  // Check if expense exists today
  const checkIfExpenseExistsToday = async () => {
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=check-today-expense",
        {
          student_id: studentData.studentId,
          date: moment().format("YYYY-MM-DD"),
        }
      );
      return response.data.exists || false;
    } catch (error) {
      console.error("Error checking today's expense:", error);
      return false;
    }
  };

  // Handle Expense Submission
  const handleExpenseSubmit = async (event) => {
    event.preventDefault();

    const {
      expensePayable,
      expensePaid,
      discountType,
      discountValue,
      paidMonth,
      expenseType,
      description,
    } = expenseData;

    const expensePayableValue = parseFloat(expensePayable) || 0;
    const expensePaidValue = parseFloat(expensePaid) || 0;
    const discountAmount = parseFloat(discountValue) || 0;

    // Calculate discount amount
    let calculatedDiscount = 0;
    if (discountType === "percentage") {
      calculatedDiscount = (expensePayableValue * discountAmount) / 100;
    } else {
      calculatedDiscount = discountAmount;
    }

    calculatedDiscount = Math.min(calculatedDiscount, expensePayableValue);
    const discountedPayable = expensePayableValue - calculatedDiscount;

    // Validations
    if (!expensePayableValue || expensePayableValue <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Please enter a valid expense payable amount.",
        icon: "warning",
      });
      return;
    }

    if (!expensePaidValue || expensePaidValue <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Please enter a valid expense paid amount.",
        icon: "warning",
      });
      return;
    }

    if (expensePaidValue > discountedPayable) {
      Swal.fire({
        title: "Amount Exceeded",
        text: `Expense Paid cannot exceed ${formatCurrency(
          discountedPayable
        )}.`,
        icon: "error",
      });
      return;
    }

    // If there's a parent entry, validate against remaining balance
    if (parentEntry) {
      const remainingBalance = parentTotals.remainingBalance;
      if (expensePaidValue > remainingBalance) {
        Swal.fire({
          title: "Payment Exceeds Balance",
          text: `You cannot pay more than ${formatCurrency(
            remainingBalance
          )}. Remaining balance from parent entry.`,
          icon: "error",
        });
        return;
      }
    }

    const newPending = discountedPayable - expensePaidValue;

    const expenseSubmissionData = {
      student_id: studentData.studentId,
      student_name: studentData.studentName,
      family_name: studentData.fatherName || "",
      family_code: studentData.family_status || "",
      expense_payable: expensePayableValue,
      expense_paid: expensePaidValue,
      expense_pending: newPending,
      discount_type: discountType,
      discount_value: discountAmount,
      discount_amount: calculatedDiscount,
      paid_month: paidMonth,
      expense_type: expenseType,
      description: description || "",
    };

    console.log("Submitting expense:", expenseSubmissionData);

    setIsSubmitting(true);

    try {
      const duplicateCheck = await checkIfExpenseExistsToday();

      if (duplicateCheck) {
        const confirmResult = await Swal.fire({
          title: "Duplicate Transaction",
          html: `
            <div class="studentExpense-duplicate-warning">
              <FaExclamationTriangle style="color: #f39c12; font-size: 2rem; margin-bottom: 1rem;" />
              <h3>Transaction Already Made Today</h3>
              <p>An expense transaction has already been recorded for today for this student.</p>
              <p><strong>Do you still want to make this payment?</strong></p>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, Submit Anyway",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        });

        if (!confirmResult.isConfirmed) {
          setIsSubmitting(false);
          return;
        }
      }

      // Submit new expense
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=submit-solo-expense",
        expenseSubmissionData
      );

      console.log("Submit response:", response.data);

      if (response.data.success) {
        Swal.fire({
          title: "Success!",
          html: `
            <div class="studentExpense-successModal">
              <FaCheckCircle style="color: #27ae60; font-size: 3rem; margin-bottom: 1rem;" />
              <h3>Expense Payment Successful!</h3>
              <div class="studentExpense-receiptInfo">
                <div><strong>Previous Balance:</strong> ${formatCurrency(
                  expensePayableValue
                )}</div>
                ${
                  discountAmount > 0
                    ? `<div><strong>Discount Applied:</strong> ${
                        discountType === "percentage"
                          ? discountAmount + "%"
                          : formatCurrency(discountAmount)
                      } (${formatCurrency(calculatedDiscount)})</div>`
                    : ""
                }
                <div><strong>Amount Payable:</strong> ${formatCurrency(
                  discountedPayable
                )}</div>
                <div><strong>Amount Paid:</strong> ${formatCurrency(
                  expensePaidValue
                )}</div>
                <div><strong>New Balance:</strong> ${formatCurrency(
                  newPending
                )}</div>
              </div>
            </div>
          `,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });

        // Refresh data after successful submission
        fetchExpenseData();
        if (onRefresh) onRefresh();
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.error || "Failed to submit expense data.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Expense Submission Error:", error);
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.error ||
          "An error occurred while submitting expense data.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Edit Click
  const handleEditClick = (record) => {
    console.log("Editing record:", record);

    // Check if this is parent entry
    const isParent = parentEntry && record.id === parentEntry.id;

    setEditingExpense({
      ...record,
      isParentEntry: isParent,
    });
    setIsParentEdit(isParent);
    setShowEditModal(true);

    // Initialize edit form data
    const payable = parseFloat(record.expense_payable) || 0;
    const paid = parseFloat(record.expense_paid) || 0;
    const discountType = record.discount_type || "percentage";
    const discountValue = parseFloat(record.discount_value) || 0;
    const discountAmount = parseFloat(record.discount_amount) || 0;
    const pending = parseFloat(record.expense_pending) || 0;

    // Calculate discounted payable
    const discountedPayable = Math.max(0, payable - discountAmount);

    setEditFormData({
      expensePayable: payable.toString(),
      expensePaid: paid.toString(),
      discountType: discountType,
      discountValue: discountValue.toString(),
      discountAmount: discountAmount.toString(),
      discountedPayable: discountedPayable.toString(),
      expensePending: pending.toString(),
    });
  };

  // Handle Update Expense with Parent-Child Logic
  const handleUpdateExpense = async (updatedData) => {
    setIsProcessingChainUpdate(true);
    try {
      // Prepare payload for chain update
      const payload = {
        id: updatedData.id,
        student_id: updatedData.student_id,
        expense_payable: parseFloat(updatedData.expense_payable) || 0,
        expense_paid: parseFloat(updatedData.expense_paid) || 0,
        expense_pending: parseFloat(updatedData.expense_pending) || 0,
        discount_type: updatedData.discount_type || "percentage",
        discount_value: parseFloat(updatedData.discount_value) || 0,
        discount_amount: parseFloat(updatedData.discount_amount) || 0,
        paid_month: updatedData.paid_month,
        expense_type: updatedData.expense_type || "other",
        description: updatedData.description || "",
        is_parent_entry: updatedData.isParentEntry || false,
        // Include family fields if they exist
        ...(updatedData.family_name && {
          family_name: updatedData.family_name,
        }),
        ...(updatedData.family_code && {
          family_code: updatedData.family_code,
        }),
        // Include student_name if needed
        ...(updatedData.student_name && {
          student_name: updatedData.student_name,
        }),
      };

      console.log("Sending chain update payload:", payload);

      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=update-expense",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        Swal.fire({
          title: "Success!",
          html: `
            <div class="studentExpense-successModal">
              <FaCheckCircle style="color: #27ae60; font-size: 3rem; margin-bottom: 1rem;" />
              <h3>Expense Updated Successfully!</h3>
              <div class="studentExpense-receiptInfo">
                <div><strong>Record Type:</strong> ${
                  updatedData.isParentEntry ? "Parent Entry" : "Child Entry"
                }</div>
                <div><strong>Chain Update:</strong> ${
                  response.data.summary?.record_count || 0
                } records adjusted</div>
                <div><strong>New Total Payable:</strong> ${formatCurrency(
                  response.data.summary?.total_payable || 0
                )}</div>
                <div><strong>New Total Paid:</strong> ${formatCurrency(
                  response.data.summary?.total_paid || 0
                )}</div>
                <div><strong>Remaining Balance:</strong> ${formatCurrency(
                  response.data.summary?.remaining_balance || 0
                )}</div>
              </div>
            </div>
          `,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });

        fetchExpenseData();
        setShowEditModal(false);
        setEditingExpense(null);
        setIsParentEdit(false);
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.error || "Failed to update expense.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.error || "Failed to update expense record.",
        icon: "error",
      });
    } finally {
      setIsProcessingChainUpdate(false);
    }
  };

  // Handle Edit Form Submission with Parent-Child Validation
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();

    // Validate parent entry update
    if (isParentEdit) {
      const newTotalPayable = parseFloat(editFormData.discountedPayable) || 0;
      const newParentPaid = parseFloat(editFormData.expensePaid) || 0;

      // Calculate total paid from other records
      const otherRecordsTotal = expenseRecords.reduce((total, record) => {
        if (record.id !== editingExpense.id) {
          return total + parseFloat(record.expense_paid || 0);
        }
        return total;
      }, 0);

      const newTotalPaid = otherRecordsTotal + newParentPaid;

      if (newTotalPaid > newTotalPayable) {
        Swal.fire({
          title: "Validation Error",
          html: `
            <div class="studentExpense-validationError">
              <FaExclamationTriangle style="color: #e74c3c; font-size: 2rem; margin-bottom: 1rem;" />
              <h3>Total Payments Exceed Amount</h3>
              <div class="studentExpense-errorDetails">
                <div><strong>New Total Payable:</strong> ${formatCurrency(
                  newTotalPayable
                )}</div>
                <div><strong>New Total Paid:</strong> ${formatCurrency(
                  newTotalPaid
                )}</div>
                <div><strong>Excess:</strong> ${formatCurrency(
                  newTotalPaid - newTotalPayable
                )}</div>
              </div>
              <p>Please adjust the payment amounts to stay within the total payable.</p>
            </div>
          `,
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
    }

    // Prepare update data
    const updatedData = {
      id: editingExpense.id,
      student_id: editingExpense.student_id,
      expense_payable: parseFloat(editFormData.expensePayable) || 0,
      expense_paid: parseFloat(editFormData.expensePaid) || 0,
      expense_pending: parseFloat(editFormData.expensePending) || 0,
      discount_type: editFormData.discountType,
      discount_value: parseFloat(editFormData.discountValue) || 0,
      discount_amount: parseFloat(editFormData.discountAmount) || 0,
      paid_month: editingExpense.paid_month,
      expense_type: editingExpense.expense_type || "other",
      description: editingExpense.description || "",
      isParentEntry: isParentEdit,
      // Preserve existing fields
      student_name: editingExpense.student_name || editingExpense.studentName,
      family_name: editingExpense.family_name || editingExpense.familyName,
      family_code: editingExpense.family_code || editingExpense.familyCode,
    };

    console.log("Chain update data prepared:", updatedData);
    await handleUpdateExpense(updatedData);
  };

  // Handle Delete Expense with Parent Protection
  const handleDeleteExpense = async (recordId, isParent) => {
    if (isParent) {
      Swal.fire({
        title: "Cannot Delete Parent Entry",
        html: `
          <div class="studentExpense-parent-delete-warning">
            <FaLock style="color: #e74c3c; font-size: 2rem; margin-bottom: 1rem;" />
            <h3>Parent Entry Cannot Be Deleted</h3>
            <p>This is the parent expense entry. It establishes the total amount to be paid.</p>
            <p>You can only update this entry, not delete it.</p>
            <p><strong>Total Payable:</strong> ${formatCurrency(
              parentTotals.parentAdjustedPayable
            )}</p>
          </div>
        `,
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Delete Expense",
      html: `
        <div class="studentExpense-delete-confirm">
          <FaExclamationTriangle style="color: #f39c12; font-size: 2rem; margin-bottom: 1rem;" />
          <h3>Delete Expense Record?</h3>
          <p>This will remove the expense record from the system.</p>
          <p><strong>Note:</strong> Subsequent entries will need to be adjusted manually.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete It",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (confirmResult.isConfirmed) {
      try {
        const response = await axios.post(
          "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=delete-expense",
          { id: recordId }
        );

        if (response.data.success) {
          Swal.fire({
            title: "Deleted!",
            text: "Expense record has been deleted.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });

          fetchExpenseData();
          if (onRefresh) onRefresh();
        } else {
          Swal.fire({
            title: "Error",
            text: response.data.error || "Failed to delete expense record.",
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to delete expense record.",
          icon: "error",
        });
      }
    }
  };

  // View Record Details
  const handleViewRecord = (record) => {
    const isParent = parentEntry && record.id === parentEntry.id;
    setSelectedRecord({
      ...record,
      isParentEntry: isParent,
    });
  };

  // Filter and sort records
  const getFilteredRecords = () => {
    let filtered = [...expenseRecords];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (record) =>
          record.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          record.expense_type
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          record.paid_month?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply month filter
    if (filterMonth !== "all") {
      filtered = filtered.filter((record) => record.paid_month === filterMonth);
    }

    // Apply year filter
    if (filterYear !== "all") {
      filtered = filtered.filter(
        (record) => moment(record.created_at).year() === filterYear
      );
    }

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((record) => {
        const pending = parseFloat(record.expense_pending);
        if (filterStatus === "paid") return pending === 0;
        if (filterStatus === "pending") return pending > 0;
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case "expense_payable":
        case "expense_paid":
        case "expense_pending":
          aValue = parseFloat(a[sortField]) || 0;
          bValue = parseFloat(b[sortField]) || 0;
          break;
        case "paid_month":
          const months = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ];
          aValue = months.indexOf(a.paid_month);
          bValue = months.indexOf(b.paid_month);
          break;
        default:
          aValue = a[sortField] || "";
          bValue = b[sortField] || "";
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  // Calculate totals with parent-child awareness
  const calculateTotals = () => {
    const records = getFilteredRecords();
    const totalPayable = parentTotals.parentAdjustedPayable || 0;
    const totalPaid = records.reduce(
      (sum, r) => sum + parseFloat(r.expense_paid || 0),
      0
    );
    const totalPending = parentTotals.remainingBalance || 0;

    const totalDiscount = records.reduce((sum, r) => {
      const discount = parseFloat(r.discount_amount || 0);
      if (discount > 0) return sum + discount;

      const payable = parseFloat(r.expense_payable || 0);
      const discountValue = parseFloat(r.discount_value || 0);
      if (r.discount_type === "percentage") {
        return sum + (payable * discountValue) / 100;
      }
      return sum + discountValue;
    }, 0);

    return {
      totalPayable,
      totalPaid,
      totalPending,
      totalDiscount,
      count: records.length,
    };
  };

  // Get record position in chain
  const getRecordPosition = (record) => {
    if (!expenseRecords.length) return 0;

    const sortedRecords = [...expenseRecords].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    const index = sortedRecords.findIndex((r) => r.id === record.id);

    return index + 1; // 1-based position
  };

  // Render parent-child chain visualization
  const renderChainVisualization = () => {
    if (expenseRecords.length <= 1) return null;

    const sortedRecords = [...expenseRecords].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    return (
      <div className="studentExpense-chain-visualization">
        <h4>
          <FaLink style={{ marginRight: "8px" }} />
          Expense Payment Chain
        </h4>
        <div className="studentExpense-chain-nodes">
          {sortedRecords.map((record, index) => {
            const isParent = index === 0;
            const isLast = index === sortedRecords.length - 1;

            return (
              <div key={record.id} className="studentExpense-chain-node">
                <div
                  className={`studentExpense-node-icon ${
                    isParent ? "parent" : "child"
                  }`}
                >
                  {isParent ? <FaLock /> : <FaLink />}
                </div>
                <div className="studentExpense-node-content">
                  <div className="studentExpense-node-header">
                    <span className="studentExpense-node-position">
                      #{index + 1}
                    </span>
                    <span className="studentExpense-node-type">
                      {isParent ? "Parent Entry" : `Transaction ${index}`}
                    </span>
                    <span className="studentExpense-node-date">
                      {moment(record.created_at).format("DD/MM/YYYY")}
                    </span>
                  </div>
                  <div className="studentExpense-node-details">
                    <div>Payable: {formatCurrency(record.expense_payable)}</div>
                    <div>Paid: {formatCurrency(record.expense_paid)}</div>
                    <div>Pending: {formatCurrency(record.expense_pending)}</div>
                  </div>
                </div>
                {!isLast && (
                  <div className="studentExpense-chain-arrow">
                    <FaArrowRight />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // If no student data is available
  if (!studentData?.studentId) {
    return (
      <div className="studentExpense-emptyState">
        <div className="studentExpense-emptyStateIcon">
          <FaMoneyBillWave />
        </div>
        <h3>No Student Selected</h3>
        <p>Please search for a student to manage their expenses.</p>
      </div>
    );
  }

  return (
    <div className="studentExpense-container">
      {/* Loading Indicator */}
      {(isLoading || isProcessingChainUpdate) && (
        <div className="studentExpense-loading">
          <div className="studentExpense-loading-spinner">
            <FaSpinner className="spinner-icon" />
          </div>
          <p>
            {isProcessingChainUpdate
              ? "Updating expense chain and adjusting subsequent entries..."
              : "Loading expense data..."}
          </p>
        </div>
      )}

      {/* Student Info Banner */}
      <div className="studentExpense-studentInfo">
        <div className="studentExpense-studentInfo-content">
          <h3>{studentData.studentName}</h3>
          <div className="studentExpense-studentInfo-details">
            <span>ID: {studentData.studentId}</span>
            <span>Family: {studentData.fatherName || "N/A"}</span>
            <span>Class: {studentData.class || "N/A"}</span>
          </div>
        </div>
        <button
          className="studentExpense-refreshBtn-main"
          onClick={handleRefresh}
        >
          <FaSync /> Refresh
        </button>
      </div>

      {/* Parent Entry Information Section */}
      {parentEntry && (
        <div className="studentExpense-parent-info">
          <div className="studentExpense-parent-info-header">
            <FaDatabase className="studentExpense-parent-icon" />
            <h4>Parent Entry Information</h4>
            <span className="studentExpense-parent-badge">
              Entry #{parentTotals.childCount + 1} in chain
            </span>
          </div>
          <div className="studentExpense-parent-details">
            <div className="studentExpense-parent-detail">
              <span>Parent Entry Date:</span>
              <span>
                {moment(parentEntry.created_at).format("DD-MM-YYYY HH:mm")}
              </span>
            </div>
            <div className="studentExpense-parent-detail">
              <span>Original Payable:</span>
              <span>{formatCurrency(parentTotals.parentPayable)}</span>
            </div>
            <div className="studentExpense-parent-detail">
              <span>Parent Discount:</span>
              <span>{formatCurrency(parentTotals.parentDiscount)}</span>
            </div>
            <div className="studentExpense-parent-detail">
              <span>Adjusted Payable:</span>
              <span>{formatCurrency(parentTotals.parentAdjustedPayable)}</span>
            </div>
            <div className="studentExpense-parent-detail">
              <span>Total Paid So Far:</span>
              <span>{formatCurrency(parentTotals.totalPaid)}</span>
            </div>
            <div className="studentExpense-parent-detail highlight">
              <span>Remaining Balance:</span>
              <span>{formatCurrency(parentTotals.remainingBalance)}</span>
            </div>
          </div>
          <div className="studentExpense-calculation-breakdown">
            <div className="studentExpense-calculation-title">
              <FaCalculator style={{ marginRight: "8px" }} />
              Calculation Breakdown
            </div>
            <div className="studentExpense-calculation-steps">
              <div className="studentExpense-calculation-step">
                <span>Parent Payable:</span>
                <strong>{formatCurrency(parentTotals.parentPayable)}</strong>
              </div>
              <div className="studentExpense-calculation-step operator">
                <span>-</span>
              </div>
              <div className="studentExpense-calculation-step">
                <span>Parent Discount:</span>
                <strong>{formatCurrency(parentTotals.parentDiscount)}</strong>
              </div>
              <div className="studentExpense-calculation-step operator">
                <span>=</span>
              </div>
              <div className="studentExpense-calculation-step">
                <span>Adjusted Payable:</span>
                <strong>
                  {formatCurrency(parentTotals.parentAdjustedPayable)}
                </strong>
              </div>
              <div className="studentExpense-calculation-step operator">
                <span>-</span>
              </div>
              <div className="studentExpense-calculation-step">
                <span>Total Paid:</span>
                <strong>{formatCurrency(parentTotals.totalPaid)}</strong>
              </div>
              <div className="studentExpense-calculation-step operator">
                <span>=</span>
              </div>
              <div className="studentExpense-calculation-step result">
                <span>Remaining Balance (Next Payable):</span>
                <strong>{formatCurrency(parentTotals.remainingBalance)}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Form */}
      <div className="studentExpense-form">
        <div className="studentExpense-formHeader">
          <FaMoneyBillWave className="studentExpense-formHeaderIcon" />
          <h3>Record Expense (Running Balance)</h3>
          {expenseRecords.length > 0 && (
            <span className="studentExpense-recordCount">
              {expenseRecords.length} record
              {expenseRecords.length !== 1 ? "s" : ""} found
            </span>
          )}
        </div>

        <form onSubmit={handleExpenseSubmit}>
          <div className="studentExpense-formRow">
            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaMoneyBillWave className="studentExpense-labelIcon" />
                Next Payable (Calculated)
                <span className="studentExpense-fieldHint">
                  (Parent Payable - Total Paid)
                </span>
              </label>
              <input
                type="text"
                className="studentExpense-formInput studentExpense-runningBalance"
                value={formatCurrency(expenseData.expensePayable)}
                readOnly
              />
              <div className="studentExpense-balanceExplanation">
                {parentEntry
                  ? `Parent Payable: ${formatCurrency(
                      parentTotals.parentAdjustedPayable
                    )} - Total Paid: ${formatCurrency(
                      parentTotals.totalPaid
                    )} = ${formatCurrency(expenseData.expensePayable)}`
                  : "No parent entry found. Starting with 0 balance."}
              </div>
            </div>

            {/* Parent Entry Summary */}
            {parentEntry && (
              <div className="studentExpense-formGroup">
                <label className="studentExpense-formLabel">
                  <FaLock className="studentExpense-labelIcon" />
                  Chain Status
                </label>
                <div className="studentExpense-parent-summary">
                  <div className="studentExpense-parent-summary-item">
                    <span>Chain Position:</span>
                    <span>{parentTotals.childCount + 1}</span>
                  </div>
                  <div className="studentExpense-parent-summary-item">
                    <span>Remaining:</span>
                    <span>{formatCurrency(parentTotals.remainingBalance)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="studentExpense-formRow">
            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaPercent className="studentExpense-labelIcon" />
                Discount Type
              </label>
              <select
                className="studentExpense-formSelect"
                value={expenseData.discountType}
                onChange={(e) => {
                  const newType = e.target.value;
                  const payable = parseFloat(expenseData.expensePayable) || 0;
                  let newDiscountValue =
                    parseFloat(expenseData.discountValue) || 0;

                  if (
                    newType === "percentage" &&
                    expenseData.discountType === "fixed"
                  ) {
                    newDiscountValue =
                      payable > 0 ? (newDiscountValue / payable) * 100 : 0;
                  } else if (
                    newType === "fixed" &&
                    expenseData.discountType === "percentage"
                  ) {
                    newDiscountValue = (payable * newDiscountValue) / 100;
                  }

                  let discountAmount = 0;
                  if (newType === "percentage") {
                    discountAmount = (payable * newDiscountValue) / 100;
                  } else {
                    discountAmount = newDiscountValue;
                  }

                  const discountedPayable = payable - discountAmount;
                  const paidValue = parseFloat(expenseData.expensePaid) || 0;
                  const pendingAmount = discountedPayable - paidValue;

                  setExpenseData((prev) => ({
                    ...prev,
                    discountType: newType,
                    discountValue: newDiscountValue.toString(),
                    expensePending: (pendingAmount >= 0
                      ? pendingAmount
                      : 0
                    ).toString(),
                  }));
                }}
              >
                <option value="fixed">Fixed Amount (Rs.)</option>
                <option value="percentage">Percentage (%)</option>
              </select>
            </div>

            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaMoneyBillWave className="studentExpense-labelIcon" />
                Discount{" "}
                {expenseData.discountType === "percentage" ? "(%)" : "(Rs.)"}
              </label>
              <input
                type="number"
                className="studentExpense-formInput"
                value={expenseData.discountValue}
                onChange={(e) => {
                  const discountValue = parseFloat(e.target.value) || 0;
                  const payable = parseFloat(expenseData.expensePayable) || 0;
                  const discountType = expenseData.discountType;

                  let discountAmount = 0;
                  if (discountType === "percentage") {
                    discountAmount = (payable * discountValue) / 100;
                  } else {
                    discountAmount = discountValue;
                  }

                  const discountedPayable = payable - discountAmount;
                  const paidValue = parseFloat(expenseData.expensePaid) || 0;
                  const pendingAmount = discountedPayable - paidValue;

                  setExpenseData((prev) => ({
                    ...prev,
                    discountValue: e.target.value,
                    expensePending: (pendingAmount >= 0
                      ? pendingAmount
                      : 0
                    ).toString(),
                  }));
                }}
                placeholder={
                  expenseData.discountType === "percentage" ? "0" : "0"
                }
                min="0"
                step="0.01"
                max={
                  expenseData.discountType === "percentage" ? "100" : undefined
                }
              />
            </div>
          </div>

          <div className="studentExpense-formRow">
            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaMoneyBillWave className="studentExpense-labelIcon" />
                Discount Amount
              </label>
              <input
                type="text"
                className="studentExpense-formInput studentExpense-discountedAmount"
                value={formatCurrency(calculateExpenseDiscountAmount())}
                readOnly
              />
            </div>

            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaMoneyBillWave className="studentExpense-labelIcon" />
                Amount After Discount
              </label>
              <input
                type="text"
                className="studentExpense-formInput studentExpense-discountedAmount"
                value={formatCurrency(calculateDiscountedExpense())}
                readOnly
              />
            </div>
          </div>

          <div className="studentExpense-formRow">
            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaMoneyBillWave className="studentExpense-labelIcon" />
                Expense Paid
              </label>
              <input
                type="number"
                className="studentExpense-formInput"
                value={expenseData.expensePaid}
                onChange={(e) => handleExpensePaidChange(e.target.value)}
                placeholder="Enter amount"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaMoneyBillWave className="studentExpense-labelIcon" />
                New Balance (Next Payable)
              </label>
              <input
                type="text"
                className="studentExpense-formInput studentExpense-newBalance"
                value={formatCurrency(expenseData.expensePending)}
                readOnly
              />
              <div className="studentExpense-balanceExplanation">
                This will be the opening balance for the next entry
              </div>
            </div>
          </div>

          <div className="studentExpense-formRow">
            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaCalendarAlt className="studentExpense-labelIcon" />
                Paid Month
              </label>
              <select
                className="studentExpense-formSelect"
                value={expenseData.paidMonth}
                onChange={(e) =>
                  setExpenseData((prev) => ({
                    ...prev,
                    paidMonth: e.target.value,
                  }))
                }
                required
              >
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            <div className="studentExpense-formGroup">
              <label className="studentExpense-formLabel">
                <FaMoneyBillWave className="studentExpense-labelIcon" />
                Expense Type
              </label>
              <select
                className="studentExpense-formSelect"
                value={expenseData.expenseType}
                onChange={(e) =>
                  setExpenseData((prev) => ({
                    ...prev,
                    expenseType: e.target.value,
                  }))
                }
              >
                <option value="books">Books</option>
                <option value="uniform">Uniform</option>
                <option value="transport">Transport</option>
                <option value="exam">Exam Fee</option>
                <option value="activity">Activity</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="studentExpense-formGroup">
            <label className="studentExpense-formLabel">
              <FaEdit className="studentExpense-labelIcon" />
              Description (Optional)
            </label>
            <textarea
              className="studentExpense-formTextarea"
              value={expenseData.description}
              onChange={(e) =>
                setExpenseData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Enter expense description..."
              rows="2"
            />
          </div>

          <div className="studentExpense-formActions">
            <button
              type="submit"
              className="studentExpense-submitBtn"
              disabled={
                !expenseData.expensePaid ||
                isSubmitting ||
                parentTotals.remainingBalance <= 0
              }
              title={
                parentTotals.remainingBalance <= 0
                  ? "No remaining balance to pay"
                  : ""
              }
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="spinner-icon" />
                  Processing...
                </>
              ) : (
                <>
                  <FaPaperPlane className="studentExpense-submitIcon" />
                  Submit Expense
                </>
              )}
            </button>
            <button
              type="button"
              className="studentExpense-historyBtn"
              onClick={() => setShowExpenseModal(true)}
              disabled={expenseRecords.length === 0}
            >
              <FaHistory className="studentExpense-historyIcon" />
              View & Manage Records
            </button>
          </div>
        </form>
      </div>

      {/* Chain Visualization */}
      {renderChainVisualization()}

      {/* Recent Expenses */}
      {expenseRecords.length > 0 && (
        <div className="studentExpense-recentExpenses">
          <div className="studentExpense-recentHeader">
            <FaHistory className="studentExpense-recentIcon" />
            <h3>Recent Expense Records</h3>
            <button
              className="studentExpense-refreshBtn"
              onClick={handleRefresh}
              title="Refresh"
            >
              <FaSync />
            </button>
          </div>

          <div className="studentExpense-expenseTable">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Month</th>
                  <th>Type</th>
                  <th>Payable</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenseRecords
                  .slice()
                  .sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                  )
                  .slice(0, 5)
                  .map((record, index) => {
                    const isParent =
                      parentEntry && record.id === parentEntry.id;
                    const position = getRecordPosition(record);

                    return (
                      <tr
                        key={index}
                        className={isParent ? "studentExpense-parent-row" : ""}
                      >
                        <td>
                          {isParent ? (
                            <span
                              className="studentExpense-parent-badge"
                              title="Parent Entry"
                            >
                              <FaLock />
                            </span>
                          ) : (
                            <span className="studentExpense-position-badge">
                              #{position}
                            </span>
                          )}
                        </td>
                        <td>{record.paid_month}</td>
                        <td>
                          <span
                            className={`studentExpense-expenseTypeTag ${
                              record.expense_type || "other"
                            }`}
                          >
                            {record.expense_type || "Other"}
                          </span>
                        </td>
                        <td>{formatCurrency(record.expense_payable)}</td>
                        <td>{formatCurrency(record.expense_paid)}</td>
                        <td>
                          <span
                            className={`studentExpense-balance ${
                              parseFloat(record.expense_pending) === 0
                                ? "zero"
                                : ""
                            }`}
                          >
                            {formatCurrency(record.expense_pending)}
                          </span>
                        </td>
                        <td>
                          {moment(record.created_at).format("DD-MM-YYYY")}
                        </td>
                        <td>
                          <div className="studentExpense-actions">
                            {/* NEW PRINT BUTTON */}
                          <button 
                            className="studentExpense-printBtn-row" 
                            onClick={() => handlePrintExpense(record)}
                            title="Print Voucher"
                            style={{ color: "#1a2a6c", background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem" }}
                          >
                          
                            <FaPrint />
                          </button>
                            <button
                              className="studentExpense-viewBtn"
                              onClick={() => handleViewRecord(record)}
                              title="View Details"
                            >
                              <FaEye />
                            </button>
                            <button
                              className="studentExpense-editBtn"
                              onClick={() => handleEditClick(record)}
                              title="Edit Record"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="studentExpense-deleteBtn"
                              onClick={() =>
                                handleDeleteExpense(record.id, isParent)
                              }
                              title="Delete Record"
                              disabled={isParent}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {expenseRecords.length > 5 && (
            <div className="studentExpense-viewAll">
              <button onClick={() => setShowExpenseModal(true)}>
                View All Records ({expenseRecords.length} entries)
                <FaExternalLinkAlt style={{ marginLeft: "8px" }} />
              </button>
            </div>
          )}

          {/* Current Balance Summary */}
          <div className="studentExpense-currentBalance">
            <div className="studentExpense-currentBalanceLabel">
              <FaMoneyBillWave style={{ marginRight: "8px" }} />
              Current Balance (Next Entry's Payable):
            </div>
            <div className="studentExpense-currentBalanceValue">
              {formatCurrency(expenseData.expensePayable)}
            </div>
          </div>
        </div>
      )}

      {/* Expense Records Modal */}
      {showExpenseModal && (
        <div className="studentExpense-modal-overlay">
          <div className="studentExpense-modal">
            <div className="studentExpense-modal-header">
              <div className="studentExpense-modal-header-left">
                <FaHistory className="studentExpense-modal-icon" />
                <div>
                  <h3>Expense Records - {studentData?.studentName}</h3>
                  <p>Student ID: {studentData?.studentId}</p>
                </div>
              </div>
              <button
                className="studentExpense-modal-close"
                onClick={() => setShowExpenseModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="studentExpense-modal-filters">
              <div className="studentExpense-search-box">
                <FaSearch className="studentExpense-search-icon" />
                <input
                  type="text"
                  placeholder="Search by description, type, or month..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="studentExpense-search-input"
                />
              </div>

              <div className="studentExpense-filter-controls">
                <div className="studentExpense-filter-group">
                  <label>Month</label>
                  <select
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                  >
                    <option value="all">All Months</option>
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="studentExpense-filter-group">
                  <label>Year</label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                  >
                    <option value="all">All Years</option>
                    {[2022, 2023, 2024, 2025, 2026].map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="studentExpense-filter-group">
                  <label>Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                <button
                  className="studentExpense-filter-clear"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterMonth("all");
                    setFilterYear("all");
                    setFilterStatus("all");
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Parent Entry Summary Section */}
            {parentEntry && (
              <div className="studentExpense-modal-parent-summary">
                <div className="studentExpense-parent-summary-header">
                  <FaLock className="studentExpense-parent-summary-icon" />
                  <h4>Parent Entry Summary</h4>
                  <span className="studentExpense-parent-badge-label">
                    Parent Entry Protected
                  </span>
                </div>
                <div className="studentExpense-parent-summary-content">
                  <div className="studentExpense-parent-summary-item">
                    <span>Parent Entry Date:</span>
                    <span>
                      {moment(parentEntry.created_at).format(
                        "DD-MM-YYYY HH:mm"
                      )}
                    </span>
                  </div>
                  <div className="studentExpense-parent-summary-item">
                    <span>Original Payable:</span>
                    <span>{formatCurrency(parentTotals.parentPayable)}</span>
                  </div>
                  <div className="studentExpense-parent-summary-item">
                    <span>Total Discount:</span>
                    <span>{formatCurrency(parentTotals.parentDiscount)}</span>
                  </div>
                  <div className="studentExpense-parent-summary-item">
                    <span>Adjusted Payable:</span>
                    <span>
                      {formatCurrency(parentTotals.parentAdjustedPayable)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="studentExpense-modal-summary">
              {(() => {
                const totals = calculateTotals();
                return (
                  <>
                    <div className="studentExpense-summary-card">
                      <span className="studentExpense-summary-label">
                        Total Records
                      </span>
                      <span className="studentExpense-summary-value">
                        {totals.count}
                      </span>
                    </div>
                    <div className="studentExpense-summary-card">
                      <span className="studentExpense-summary-label">
                        Total Payable
                      </span>
                      <span className="studentExpense-summary-value">
                        {formatCurrency(totals.totalPayable)}
                      </span>
                    </div>
                    <div className="studentExpense-summary-card">
                      <span className="studentExpense-summary-label">
                        Total Paid
                      </span>
                      <span className="studentExpense-summary-value">
                        {formatCurrency(totals.totalPaid)}
                      </span>
                    </div>
                    <div className="studentExpense-summary-card">
                      <span className="studentExpense-summary-label">
                        Total Discount
                      </span>
                      <span className="studentExpense-summary-value">
                        {formatCurrency(totals.totalDiscount)}
                      </span>
                    </div>
                    <div className="studentExpense-summary-card studentExpense-summary-highlight">
                      <span className="studentExpense-summary-label">
                        Current Balance
                      </span>
                      <span className="studentExpense-summary-value">
                        {formatCurrency(totals.totalPending)}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="studentExpense-table-container">
              <table className="studentExpense-modal-table">
                <thead>
                  <tr>
                    {[
                      { key: "position", label: "#", sortable: false },
                      { key: "created_at", label: "Date", sortable: true },
                      { key: "paid_month", label: "Month", sortable: true },
                      { key: "expense_type", label: "Type", sortable: false },
                      {
                        key: "description",
                        label: "Description",
                        sortable: false,
                      },
                      {
                        key: "expense_payable",
                        label: "Payable",
                        sortable: true,
                      },
                      {
                        key: "discount_value",
                        label: "Discount",
                        sortable: false,
                      },
                      { key: "expense_paid", label: "Paid", sortable: true },
                      {
                        key: "expense_pending",
                        label: "Balance",
                        sortable: true,
                      },
                      { key: "status", label: "Status", sortable: false },
                      { key: "actions", label: "Actions", sortable: false },
                    ].map((col) => (
                      <th key={col.key}>
                        {col.sortable ? (
                          <button
                            className="studentExpense-sort-btn"
                            onClick={() => {
                              if (sortField === col.key) {
                                setSortOrder(
                                  sortOrder === "asc" ? "desc" : "asc"
                                );
                              } else {
                                setSortField(col.key);
                                setSortOrder("desc");
                              }
                            }}
                          >
                            {col.label}
                            {sortField === col.key &&
                              (sortOrder === "asc" ? (
                                <FaSortUp />
                              ) : (
                                <FaSortDown />
                              ))}
                            {sortField !== col.key && <FaSort />}
                          </button>
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sortedRecords = [...getFilteredRecords()].sort(
                      (a, b) => {
                        if (sortField === "created_at") {
                          const aDate = new Date(a.created_at);
                          const bDate = new Date(b.created_at);
                          return sortOrder === "asc"
                            ? aDate - bDate
                            : bDate - aDate;
                        }
                        return 0;
                      }
                    );

                    return sortedRecords.map((record, index) => {
                      const payable = parseFloat(record.expense_payable) || 0;
                      const discountType = record.discount_type || "percentage";
                      const discountValue = parseFloat(
                        record.discount_value || 0
                      );
                      const discountAmount = parseFloat(
                        record.discount_amount || 0
                      );
                      const isParent =
                        parentEntry && record.id === parentEntry.id;
                      const position = getRecordPosition(record);

                      let calculatedDiscount = discountAmount;
                      if (!calculatedDiscount && discountValue) {
                        if (discountType === "percentage") {
                          calculatedDiscount = (payable * discountValue) / 100;
                        } else {
                          calculatedDiscount = discountValue;
                        }
                      }

                      const discountDisplay =
                        discountType === "percentage"
                          ? `${discountValue}%`
                          : formatCurrency(discountValue);

                      return (
                        <tr
                          key={record.id}
                          className={
                            isParent ? "studentExpense-parent-row" : ""
                          }
                        >
                          <td>
                            {isParent ? (
                              <span
                                className="studentExpense-parent-badge-modal"
                                title="Parent Entry"
                              >
                                <FaLock /> P
                              </span>
                            ) : (
                              <span className="studentExpense-position-badge-modal">
                                #{position}
                              </span>
                            )}
                          </td>
                          <td>
                            {moment(record.created_at).format("DD-MM-YYYY")}
                          </td>
                          <td>{record.paid_month}</td>
                          <td>
                            <span
                              className={`studentExpense-expenseTypeTag ${
                                record.expense_type || "other"
                              }`}
                            >
                              {record.expense_type || "Other"}
                            </span>
                          </td>
                          <td className="studentExpense-description-cell">
                            {record.description || "-"}
                          </td>
                          <td>{formatCurrency(payable)}</td>
                          <td>{discountDisplay}</td>
                          <td>{formatCurrency(record.expense_paid)}</td>
                          <td>
                            <span
                              className={`studentExpense-balance ${
                                parseFloat(record.expense_pending) === 0
                                  ? "zero"
                                  : ""
                              }`}
                            >
                              {formatCurrency(record.expense_pending)}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`studentExpense-statusBadge ${
                                parseFloat(record.expense_pending) === 0
                                  ? "paid"
                                  : "pending"
                              }`}
                            >
                              {parseFloat(record.expense_pending) === 0
                                ? "Paid"
                                : "Pending"}
                            </span>
                            {isParent && (
                              <span className="studentExpense-parent-label">
                                Parent
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="studentExpense-actions">
                              <button
                                className="studentExpense-viewBtn"
                                onClick={() => handleViewRecord(record)}
                                title="View Details"
                              >
                                <FaEye />
                              </button>
                              <button
                                className="studentExpense-editBtn"
                                onClick={() => handleEditClick(record)}
                                title="Edit Record"
                              >
                                <FaEdit />
                              </button>
                              <button
                                className="studentExpense-deleteBtn"
                                onClick={() =>
                                  handleDeleteExpense(record.id, isParent)
                                }
                                title="Delete Record"
                                disabled={isParent}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>

              {getFilteredRecords().length === 0 && (
                <div className="studentExpense-noRecords">
                  <FaInfoCircle className="studentExpense-noRecords-icon" />
                  <p>No expense records found for the selected filters.</p>
                  <button
                    className="studentExpense-clearFiltersBtn"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterMonth("all");
                      setFilterYear("all");
                      setFilterStatus("all");
                    }}
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>

            <div className="studentExpense-modal-footer">
              <div className="studentExpense-export-options">
                <button className="studentExpense-export-btn">
                  <FaReceipt style={{ marginRight: "8px" }} />
                  Export to Excel
                </button>
                <button className="studentExpense-print-btn">
                  Print Records
                </button>
              </div>
              <div className="studentExpense-record-count">
                Showing {getFilteredRecords().length} of {expenseRecords.length}{" "}
                records
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Expense Modal */}
      {showEditModal && editingExpense && (
        <div className="studentExpense-edit-modal-overlay">
          <div className="studentExpense-edit-modal">
            <div className="studentExpense-edit-modal-header">
              <div className="studentExpense-edit-modal-header-left">
                {isParentEdit ? (
                  <FaLock className="studentExpense-edit-modal-icon parent-icon" />
                ) : (
                  <FaEdit className="studentExpense-edit-modal-icon" />
                )}
                <div>
                  <h3>
                    {isParentEdit
                      ? "Edit Parent Expense Entry"
                      : "Edit Expense Record"}
                  </h3>
                  <p className="studentExpense-edit-subtitle">
                    {isParentEdit
                      ? "Changing this entry will adjust all subsequent entries in the chain"
                      : `Position: #${getRecordPosition(
                          editingExpense
                        )} in the payment chain`}
                  </p>
                </div>
              </div>
              <button
                className="studentExpense-edit-modal-close"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingExpense(null);
                  setIsParentEdit(false);
                  setEditFormData({
                    expensePayable: "0",
                    expensePaid: "0",
                    discountType: "percentage",
                    discountValue: "0",
                    discountAmount: "0",
                    discountedPayable: "0",
                    expensePending: "0",
                  });
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="studentExpense-edit-modal-content">
              {/* Parent Entry Warning */}
              {isParentEdit && (
                <div className="studentExpense-parent-warning">
                  <FaExclamationTriangle className="studentExpense-parent-warning-icon" />
                  <div className="studentExpense-parent-warning-content">
                    <h4>Parent Entry Alert</h4>
                    <p>
                      This is the parent entry. Changing the payable amount will
                      update the total target and adjust all subsequent entries
                      in the payment chain.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleEditFormSubmit}>
                <div className="studentExpense-edit-form-row">
                  <div className="studentExpense-edit-form-group">
                    <label>
                      Expense Payable
                      {isParentEdit && (
                        <span className="studentExpense-field-warning">
                          (Will adjust total target)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={editFormData.expensePayable}
                      onChange={(e) =>
                        handleEditFieldChange("expensePayable", e.target.value)
                      }
                      step="0.01"
                      min="0"
                      required
                      disabled={!isParentEdit && expenseRecords.length > 0}
                      className={
                        !isParentEdit ? "studentExpense-readonly-input" : ""
                      }
                    />
                    {!isParentEdit && expenseRecords.length > 0 && (
                      <div className="studentExpense-edit-hint">
                        Payable is automatically set from previous entry's
                        balance
                      </div>
                    )}
                  </div>
                  <div className="studentExpense-edit-form-group">
                    <label>Expense Paid</label>
                    <input
                      type="number"
                      value={editFormData.expensePaid}
                      onChange={(e) =>
                        handleEditFieldChange("expensePaid", e.target.value)
                      }
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="studentExpense-edit-form-row">
                  <div className="studentExpense-edit-form-group">
                    <label>Discount Type</label>
                    <select
                      value={editFormData.discountType}
                      onChange={(e) =>
                        handleEditFieldChange("discountType", e.target.value)
                      }
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (Rs.)</option>
                    </select>
                  </div>
                  <div className="studentExpense-edit-form-group">
                    <label>
                      Discount Value{" "}
                      {editFormData.discountType === "percentage"
                        ? "(%)"
                        : "(Rs.)"}
                    </label>
                    <input
                      type="number"
                      value={editFormData.discountValue}
                      onChange={(e) =>
                        handleEditFieldChange("discountValue", e.target.value)
                      }
                      step="0.01"
                      min="0"
                      max={
                        editFormData.discountType === "percentage"
                          ? "100"
                          : undefined
                      }
                      required
                    />
                  </div>
                </div>

                <div className="studentExpense-edit-form-row">
                  <div className="studentExpense-edit-form-group">
                    <label>Discount Amount</label>
                    <input
                      type="text"
                      value={formatCurrency(editFormData.discountAmount)}
                      readOnly
                      className="studentExpense-readonly-input"
                    />
                  </div>
                  <div className="studentExpense-edit-form-group">
                    <label>Amount After Discount</label>
                    <input
                      type="text"
                      value={formatCurrency(editFormData.discountedPayable)}
                      readOnly
                      className="studentExpense-readonly-input"
                    />
                  </div>
                </div>

                <div className="studentExpense-edit-form-row">
                  <div className="studentExpense-edit-form-group">
                    <label>Pending Balance</label>
                    <input
                      type="text"
                      value={formatCurrency(editFormData.expensePending)}
                      readOnly
                      className="studentExpense-readonly-input studentExpense-pending-highlight"
                    />
                    <div className="studentExpense-edit-hint">
                      Calculated automatically: (Payable - Discount) - Paid
                    </div>
                  </div>
                  {isParentEdit && (
                    <div className="studentExpense-edit-form-group">
                      <label>Chain Impact</label>
                      <div className="studentExpense-chain-impact">
                        <FaLink className="studentExpense-chain-impact-icon" />
                        <span>
                          Will affect {expenseRecords.length - 1} subsequent
                          entries
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="studentExpense-edit-form-row">
                  <div className="studentExpense-edit-form-group">
                    <label>Month</label>
                    <select
                      value={editingExpense.paid_month}
                      onChange={(e) => {
                        setEditingExpense((prev) => ({
                          ...prev,
                          paid_month: e.target.value,
                        }));
                      }}
                    >
                      {[
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                      ].map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="studentExpense-edit-form-group">
                    <label>Expense Type</label>
                    <select
                      value={editingExpense.expense_type || "other"}
                      onChange={(e) => {
                        setEditingExpense((prev) => ({
                          ...prev,
                          expense_type: e.target.value,
                        }));
                      }}
                    >
                      <option value="books">Books</option>
                      <option value="uniform">Uniform</option>
                      <option value="transport">Transport</option>
                      <option value="exam">Exam Fee</option>
                      <option value="activity">Activity</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="studentExpense-edit-form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={editingExpense.description || ""}
                    onChange={(e) => {
                      setEditingExpense((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }));
                    }}
                    rows="3"
                    placeholder="Enter expense description..."
                  />
                </div>

                {/* Parent Entry Total Validation */}
                {isParentEdit && (
                  <div className="studentExpense-parent-validation">
                    <h4>
                      <FaCalculator style={{ marginRight: "8px" }} />
                      Total Payment Validation
                    </h4>
                    <div className="studentExpense-validation-details">
                      <div className="studentExpense-validation-row">
                        <span>New Total Payable (after discount):</span>
                        <span>
                          {formatCurrency(editFormData.discountedPayable)}
                        </span>
                      </div>
                      <div className="studentExpense-validation-row">
                        <span>Other Payments Total:</span>
                        <span>
                          {formatCurrency(
                            expenseRecords.reduce((total, record) => {
                              if (record.id !== editingExpense.id) {
                                return (
                                  total + parseFloat(record.expense_paid || 0)
                                );
                              }
                              return total;
                            }, 0)
                          )}
                        </span>
                      </div>
                      <div className="studentExpense-validation-row">
                        <span>New This Payment:</span>
                        <span>{formatCurrency(editFormData.expensePaid)}</span>
                      </div>
                      <div className="studentExpense-validation-row studentExpense-validation-total">
                        <span>Total Payments After Update:</span>
                        <span>
                          {formatCurrency(
                            expenseRecords.reduce((total, record) => {
                              if (record.id !== editingExpense.id) {
                                return (
                                  total + parseFloat(record.expense_paid || 0)
                                );
                              }
                              return total;
                            }, 0) + parseFloat(editFormData.expensePaid || 0)
                          )}
                        </span>
                      </div>
                      <div
                        className={`studentExpense-validation-status ${
                          parseFloat(editFormData.discountedPayable) >=
                          expenseRecords.reduce((total, record) => {
                            if (record.id !== editingExpense.id) {
                              return (
                                total + parseFloat(record.expense_paid || 0)
                              );
                            }
                            return total;
                          }, 0) +
                            parseFloat(editFormData.expensePaid || 0)
                            ? "valid"
                            : "invalid"
                        }`}
                      >
                        {parseFloat(editFormData.discountedPayable) >=
                        expenseRecords.reduce((total, record) => {
                          if (record.id !== editingExpense.id) {
                            return total + parseFloat(record.expense_paid || 0);
                          }
                          return total;
                        }, 0) +
                          parseFloat(editFormData.expensePaid || 0)
                          ? "✅ Total payments within limit"
                          : "⚠️ Total payments exceed limit"}
                      </div>
                    </div>
                  </div>
                )}

                <div className="studentExpense-edit-amount-summary">
                  <h4>Amount Summary</h4>
                  <div className="studentExpense-edit-summary-row">
                    <span>Payable:</span>
                    <span>{formatCurrency(editFormData.expensePayable)}</span>
                  </div>
                  <div className="studentExpense-edit-summary-row">
                    <span>
                      Discount (
                      {editFormData.discountType === "percentage"
                        ? `${editFormData.discountValue}%`
                        : "Fixed"}
                      ):
                    </span>
                    <span>{formatCurrency(editFormData.discountAmount)}</span>
                  </div>
                  <div className="studentExpense-edit-summary-row">
                    <span>After Discount:</span>
                    <span>
                      {formatCurrency(editFormData.discountedPayable)}
                    </span>
                  </div>
                  <div className="studentExpense-edit-summary-row">
                    <span>Paid:</span>
                    <span>{formatCurrency(editFormData.expensePaid)}</span>
                  </div>
                  <div className="studentExpense-edit-summary-row studentExpense-edit-summary-total">
                    <span>New Balance:</span>
                    <span>{formatCurrency(editFormData.expensePending)}</span>
                  </div>
                </div>

                <div className="studentExpense-edit-original-info">
                  <p>
                    <strong>Original Transaction:</strong>{" "}
                    {moment(editingExpense.created_at).format(
                      "DD-MM-YYYY HH:mm"
                    )}
                  </p>
                  <p>
                    <strong>Original Amounts:</strong> Payable:{" "}
                    {formatCurrency(editingExpense.expense_payable)}, Paid:{" "}
                    {formatCurrency(editingExpense.expense_paid)}, Balance:{" "}
                    {formatCurrency(editingExpense.expense_pending)}
                  </p>
                </div>

                <div className="studentExpense-edit-modal-actions">
                  <button
                    type="button"
                    className="studentExpense-edit-cancel"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingExpense(null);
                      setIsParentEdit(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="studentExpense-edit-save"
                    disabled={isProcessingChainUpdate}
                  >
                    {isProcessingChainUpdate ? (
                      <>
                        <FaSpinner className="spinner-icon" />
                        {isParentEdit ? "Updating Chain..." : "Saving..."}
                      </>
                    ) : isParentEdit ? (
                      "Update Parent & Adjust Chain"
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Record Details Modal */}
      {selectedRecord && (
        <div className="studentExpense-details-modal-overlay">
          <div className="studentExpense-details-modal">
            <div className="studentExpense-details-modal-header">
              <div className="studentExpense-details-modal-header-left">
                {selectedRecord.isParentEntry ? (
                  <FaLock className="studentExpense-details-modal-icon parent-icon" />
                ) : (
                  <FaReceipt className="studentExpense-details-modal-icon" />
                )}
                <div>
                  <h3>
                    {selectedRecord.isParentEntry
                      ? "Parent Expense Entry Details"
                      : "Expense Record Details"}
                  </h3>
                  {selectedRecord.isParentEntry && (
                    <p className="studentExpense-details-subtitle">
                      This is the parent entry that establishes the total
                      payable amount
                    </p>
                  )}
                </div>
              </div>
              <button
                className="studentExpense-details-modal-close"
                onClick={() => setSelectedRecord(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="studentExpense-details-content">
              {/* Parent Entry Badge */}
              {selectedRecord.isParentEntry && (
                <div className="studentExpense-details-parent-banner">
                  <FaLock className="studentExpense-details-parent-icon" />
                  <div>
                    <h4>Parent Entry Information</h4>
                    <p>
                      This entry sets the total payable amount of{" "}
                      <strong>
                        {formatCurrency(parentTotals.parentAdjustedPayable)}
                      </strong>
                      . All subsequent payments are linked to this amount.
                    </p>
                  </div>
                </div>
              )}

              <div className="studentExpense-details-grid">
                <div className="studentExpense-details-item">
                  <span className="studentExpense-details-label">
                    Transaction ID:
                  </span>
                  <span className="studentExpense-details-value">
                    {selectedRecord.id}
                    {selectedRecord.isParentEntry && (
                      <span className="studentExpense-details-parent-tag">
                        {" "}
                        (Parent)
                      </span>
                    )}
                  </span>
                </div>
                <div className="studentExpense-details-item">
                  <span className="studentExpense-details-label">Date:</span>
                  <span className="studentExpense-details-value">
                    {moment(selectedRecord.created_at).format(
                      "DD-MM-YYYY HH:mm"
                    )}
                  </span>
                </div>
                <div className="studentExpense-details-item">
                  <span className="studentExpense-details-label">Month:</span>
                  <span className="studentExpense-details-value">
                    {selectedRecord.paid_month}
                  </span>
                </div>
                <div className="studentExpense-details-item">
                  <span className="studentExpense-details-label">Type:</span>
                  <span className="studentExpense-details-value">
                    <span
                      className={`studentExpense-expenseTypeTag ${
                        selectedRecord.expense_type || "other"
                      }`}
                    >
                      {selectedRecord.expense_type || "Other"}
                    </span>
                  </span>
                </div>
                {selectedRecord.isParentEntry && (
                  <div className="studentExpense-details-item">
                    <span className="studentExpense-details-label">
                      Position:
                    </span>
                    <span className="studentExpense-details-value">
                      <span className="studentExpense-details-position">
                        Parent Entry
                      </span>
                    </span>
                  </div>
                )}
                <div className="studentExpense-details-item full-width">
                  <span className="studentExpense-details-label">
                    Description:
                  </span>
                  <span className="studentExpense-details-value">
                    {selectedRecord.description || "No description"}
                  </span>
                </div>
              </div>

              <div className="studentExpense-details-amounts">
                <h4>Amount Details</h4>
                <div className="studentExpense-details-amount-row">
                  <span>Payable Amount:</span>
                  <span className="studentExpense-details-amount">
                    {formatCurrency(selectedRecord.expense_payable)}
                  </span>
                </div>
                <div className="studentExpense-details-amount-row">
                  <span>Discount:</span>
                  <span className="studentExpense-details-amount studentExpense-details-discount">
                    {selectedRecord.discount_type === "percentage"
                      ? `${selectedRecord.discount_value}%`
                      : formatCurrency(selectedRecord.discount_value)}
                  </span>
                </div>
                <div className="studentExpense-details-amount-row">
                  <span>Amount After Discount:</span>
                  <span className="studentExpense-details-amount">
                    {formatCurrency(
                      selectedRecord.expense_payable -
                        (selectedRecord.discount_amount || 0)
                    )}
                  </span>
                </div>
                <div className="studentExpense-details-amount-row">
                  <span>Amount Paid:</span>
                  <span className="studentExpense-details-amount studentExpense-details-paid">
                    {formatCurrency(selectedRecord.expense_paid)}
                  </span>
                </div>
                <div className="studentExpense-details-amount-row">
                  <span>Balance:</span>
                  <span
                    className={`studentExpense-details-amount ${
                      parseFloat(selectedRecord.expense_pending) === 0
                        ? "studentExpense-details-zero"
                        : "studentExpense-details-pending"
                    }`}
                  >
                    {formatCurrency(selectedRecord.expense_pending)}
                  </span>
                </div>
              </div>

              {selectedRecord.isParentEntry && (
                <div className="studentExpense-details-parent-summary">
                  <h4>Parent Entry Impact</h4>
                  <div className="studentExpense-details-impact-grid">
                    <div className="studentExpense-details-impact-item">
                      <span>Total Chain Payable:</span>
                      <span>
                        {formatCurrency(parentTotals.parentAdjustedPayable)}
                      </span>
                    </div>
                    <div className="studentExpense-details-impact-item">
                      <span>Total Chain Paid:</span>
                      <span>{formatCurrency(parentTotals.totalPaid)}</span>
                    </div>
                    <div className="studentExpense-details-impact-item">
                      <span>Remaining Balance:</span>
                      <span>
                        {formatCurrency(parentTotals.remainingBalance)}
                      </span>
                    </div>
                    <div className="studentExpense-details-impact-item">
                      <span>Chain Entries:</span>
                      <span>{expenseRecords.length}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="studentExpense-details-status">
                <h4>Status</h4>
                <span
                  className={`studentExpense-statusBadge ${
                    parseFloat(selectedRecord.expense_pending) === 0
                      ? "paid"
                      : "pending"
                  }`}
                >
                  {parseFloat(selectedRecord.expense_pending) === 0
                    ? "Fully Paid"
                    : "Pending Payment"}
                </span>
                {parseFloat(selectedRecord.expense_pending) > 0 && (
                  <p className="studentExpense-details-pending-note">
                    <FaExclamationTriangle
                      style={{ marginRight: "8px", color: "#e74c3c" }}
                    />
                    Rs. {parseFloat(selectedRecord.expense_pending).toFixed(2)}{" "}
                    remaining to be paid
                  </p>
                )}
              </div>
            </div>

            <div className="studentExpense-details-actions">
              <button
                className="studentExpense-details-edit"
                onClick={() => {
                  handleEditClick(selectedRecord);
                  setSelectedRecord(null);
                }}
              >
                <FaEdit style={{ marginRight: "8px" }} />
                Edit Record
              </button>
              <button
                className="studentExpense-details-print"
                onClick={() => window.print()}
              >
                <FaReceipt style={{ marginRight: "8px" }} />
                Print Receipt
              </button>
              <button
                className="studentExpense-details-close"
                onClick={() => setSelectedRecord(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentExpense;
